import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import EntitySearchPicker from '../../Analytics/Comparison/EntitySearchPicker';

/**
 * Configuration modal for the Dynamic Comparison widget.
 *
 * Lets the user pick two entities, a metric, and an optional date-range
 * override. Result is persisted into the widget layout's `config` field
 * so it survives reloads and is sent on every data fetch.
 */
const ENTITY_TYPES = ['Tenant', 'Category', 'Subcategory', 'Property', 'Floor', 'Group', 'Zone', 'Vertical'];
const METRICS = [
    { value: 'sales', label: 'Sales' },
    { value: 'revenue', label: 'Revenue Share' },
    { value: 'transactions', label: 'Transactions' },
];

function emptyEntity() {
    return { type: 'Tenant', id: '', label: '' };
}

export default function DynamicComparisonConfigModal({ initial = {}, onSave, onClose }) {
    const { t } = useTranslation();
    const [entityA, setEntityA] = useState(initial.entityA || emptyEntity());
    const [entityB, setEntityB] = useState(initial.entityB || emptyEntity());
    const [metric, setMetric] = useState(initial.metric || 'sales');
    const [groupBy, setGroupBy] = useState(initial.groupBy?.[0] || 'date');
    const [useCustomRange, setUseCustomRange] = useState(!!initial.dateRange);
    const [from, setFrom] = useState(initial.dateRange?.from || '');
    const [to, setTo] = useState(initial.dateRange?.to || '');

    const canSave = entityA.id && entityB.id;

    const handleSave = () => {
        if (!canSave) return;
        const config = {
            entityA: { type: entityA.type, id: entityA.id, label: entityA.label },
            entityB: { type: entityB.type, id: entityB.id, label: entityB.label },
            metric,
            groupBy: [groupBy],
        };
        if (useCustomRange && from && to) config.dateRange = { from, to };
        onSave(config);
    };

    return (
        <div style={overlay} onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="v3-card" style={panel}>
                <div style={header}>
                    <h2 className="v3-heading" style={{ margin: 0, fontSize: 18 }}>{t('Configure Comparison')}</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={t('Close')}
                        style={closeBtn}
                    >×</button>
                </div>

                <EntityRow
                    label={t('Series A')}
                    color="#3B82F6"
                    entity={entityA}
                    onChange={setEntityA}
                />
                <EntityRow
                    label={t('Series B')}
                    color="#F59E0B"
                    entity={entityB}
                    onChange={setEntityB}
                />

                <div style={row}>
                    <Field label={t('Metric')}>
                        <select value={metric} onChange={(e) => setMetric(e.target.value)} style={select}>
                            {METRICS.map(m => <option key={m.value} value={m.value}>{t(m.label)}</option>)}
                        </select>
                    </Field>
                    <Field label={t('Granularity')}>
                        <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} style={select}>
                            <option value="date">{t('Daily')}</option>
                            <option value="month">{t('Monthly')}</option>
                            <option value="year">{t('Yearly')}</option>
                        </select>
                    </Field>
                </div>

                <div style={{ marginTop: 12 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--v3-text-muted)' }}>
                        <input
                            type="checkbox"
                            checked={useCustomRange}
                            onChange={(e) => setUseCustomRange(e.target.checked)}
                        />
                        {t('Override dashboard date range')}
                    </label>
                    {useCustomRange && (
                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={dateInput} />
                            <span style={{ color: 'var(--v3-text-muted)', alignSelf: 'center' }}>→</span>
                            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={dateInput} />
                        </div>
                    )}
                </div>

                <div style={footer}>
                    <button type="button" onClick={onClose} className="v3-btn v3-btn-ghost">{t('Cancel')}</button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={!canSave}
                        className="v3-btn v3-btn-primary"
                    >
                        {t('Save & Run')}
                    </button>
                </div>
            </div>
        </div>
    );
}

function EntityRow({ label, color, entity, onChange }) {
    const { t } = useTranslation();
    return (
        <div style={entityCard}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: color }} />
                <div className="v3-label" style={{ margin: 0 }}>{label}</div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
                <select
                    value={entity.type}
                    onChange={(e) => onChange({ type: e.target.value, id: '', label: '' })}
                    style={{ ...select, width: 140 }}
                >
                    {ENTITY_TYPES.map(et => <option key={et} value={et}>{t(et)}</option>)}
                </select>
                <EntitySearchPicker
                    type={entity.type}
                    value={{ id: entity.id, label: entity.label }}
                    onChange={(next) => onChange({ ...entity, id: next.id, label: next.label })}
                />
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <div className="v3-label">{label}</div>
            <div style={{ marginTop: 4 }}>{children}</div>
        </div>
    );
}

const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const panel = {
    width: 560, maxWidth: '92vw', maxHeight: '90vh', overflowY: 'auto',
    padding: 20, background: 'var(--v3-bg-elevated)',
};
const header = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 14, gap: 8,
};
const closeBtn = {
    cursor: 'pointer', fontSize: 22, color: 'var(--v3-text-muted)',
    background: 'transparent', border: 'none', padding: '0 6px', lineHeight: 1,
};
const entityCard = {
    padding: 12, borderRadius: 10,
    border: '1px solid var(--v3-glass-border)',
    background: 'var(--v3-bg-surface)',
    marginBottom: 10,
};
const row = { display: 'flex', gap: 16, marginTop: 10, flexWrap: 'wrap' };
const select = {
    padding: '8px 12px',
    background: 'var(--v3-bg-surface)',
    border: '1px solid var(--v3-glass-border)',
    borderRadius: 8,
    color: 'var(--v3-text-primary)',
    fontSize: 13,
    outline: 'none',
};
const dateInput = {
    padding: '6px 10px',
    background: 'var(--v3-bg-surface)',
    border: '1px solid var(--v3-glass-border)',
    borderRadius: 8,
    color: 'var(--v3-text-primary)',
    fontSize: 12,
};
const footer = {
    display: 'flex', gap: 8, justifyContent: 'flex-end',
    marginTop: 18, paddingTop: 14,
    borderTop: '1px solid var(--v3-glass-border)',
};
